<script setup lang="ts">
import { onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { DEFAULT_LOCALE } from '@/i18n'

const { t, locale } = useI18n()
const homeHref = locale.value === DEFAULT_LOCALE ? '/' : `/${locale.value}/`

/** Page atteinte après paiement (redirect Stripe côté back).
 *  On purge la persistance du parcours santé pour éviter qu'un retour ultérieur
 *  ne ré-hydrate le formulaire avec des données déjà soumises. */
onMounted(() => {
  if (typeof window === 'undefined') return
  try {
    // Balayage par préfixe : la persistance du formulaire infos
    // (smerra:journey:health:info[:vN], toutes versions, clé courante incluse) et
    // l'état de souscription scopé par produit (smerra:subscription:…:<produit>).
    for (let i = window.sessionStorage.length - 1; i >= 0; i--) {
      const key = window.sessionStorage.key(i)
      if (key && (key.startsWith('smerra:journey:health:info') || key.startsWith('smerra:subscription:'))) {
        window.sessionStorage.removeItem(key)
      }
    }
  } catch { /* quota ou storage désactivé */ }
})
</script>

<template>
  <section class="thank-you" aria-labelledby="thank-you-title">
    <div class="thank-you__card">
      <div class="thank-you__icon" aria-hidden="true">
        <svg viewBox="0 0 32 32" width="40" height="40">
          <circle cx="16" cy="16" r="14" fill="none" stroke="currentColor" stroke-width="2"/>
          <path d="M10 16l4 4 8-9" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <h1 id="thank-you-title" class="thank-you__headline">{{ t('thankYou.headline') }}</h1>
      <p class="thank-you__thanks">{{ t('thankYou.thanks') }}</p>
      <p class="thank-you__watch">{{ t('thankYou.watchMail') }}</p>
      <p class="thank-you__details">{{ t('thankYou.details') }}</p>
      <p class="thank-you__sign">
        {{ t('thankYou.signOff') }}<br />
        {{ t('thankYou.team') }}
      </p>
      <a :href="homeHref" class="thank-you__home">{{ t('thankYou.backHome') }}</a>
    </div>
  </section>
</template>

<style scoped>
.thank-you {
  min-height: 60vh;
  display: grid;
  place-items: center;
  padding: clamp(2rem, 6vw, 4rem) var(--layout-gutter);
}
.thank-you__card {
  width: 100%;
  max-width: 32rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
  padding: clamp(1.5rem, 4vw, 2.5rem);
  background: var(--color-white);
  border-radius: var(--radius-xl);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-md);
}
.thank-you__icon {
  display: grid;
  place-items: center;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: color-mix(in oklab, var(--color-peach) 18%, white);
  color: var(--primary);
}
.thank-you__headline {
  font-size: var(--text-3xl);
  font-weight: 800;
  line-height: var(--leading-tight);
  color: var(--color-chocolate);
}
.thank-you__thanks {
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--color-chocolate);
}
.thank-you__watch {
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--primary);
}
.thank-you__details {
  font-size: var(--text-base);
  line-height: var(--leading-normal);
  color: var(--color-chocolate);
  max-width: 28ch;
}
.thank-you__sign {
  font-size: var(--text-sm);
  line-height: 1.5;
  color: var(--text-muted);
  font-style: italic;
}
.thank-you__home {
  margin-top: 0.4rem;
  display: inline-flex;
  align-items: center;
  padding: 0.7rem 1.6rem;
  background: var(--primary);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text-on-primary);
  text-decoration: none;
  transition: background var(--dur-fast) var(--ease-out), transform var(--dur-base) var(--ease-spring);
}
.thank-you__home:hover {
  background: var(--primary-hover);
  transform: translateY(-1px);
}
.thank-you__home:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}
</style>
